// src/services/hrApplicationsService.js
// FuMind_GM HR - Applications & Downloads Service
// v2 Frontend Fixes: attachment/document upload actions, draft continuation, document-based application support.

const HR_APPLICATIONS_LOAD_FLOW_URL = "/tracka-flow/v1/HR_APPLICATIONS_LOAD";
const HR_APPLICATIONS_ACTION_FLOW_URL = "/tracka-flow/v1/HR_APPLICATIONS_ACTION";
const HR_APPLICATIONS_API_KEY = "";

function safeJsonParse(value, fallback = null) {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return fallback; }
}
function parseFlowArray(value) {
  if (Array.isArray(value)) return value;
  const parsed = safeJsonParse(value, []);
  return Array.isArray(parsed) ? parsed : [];
}
function readableValue(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => readableValue(item)).filter(Boolean).join(" | ");
  if (typeof value === "object") return value.message || value.error || value.detail || value.details || value.body?.message || value.error?.message || JSON.stringify(value, null, 2);
  return String(value);
}
export function normalizeUser(user = {}) {
  return {
    employeeId: user.employeeId || user.employeeCode || user.EmployeeID || user.EmployeeCode || "",
    employeeCode: user.employeeCode || user.employeeId || user.EmployeeCode || user.EmployeeID || "",
    workEmail: user.workEmail || user.email || user.WorkEmail || user.Email || "",
    email: user.email || user.workEmail || user.Email || user.WorkEmail || "",
    role: user.role || user.Role || "Employee",
    displayName: user.displayName || user.name || user.employeeName || user.EmployeeName || "",
  };
}
async function postToFlow(url, body, { allowUnconfigured = false } = {}) {
  if (!url) {
    if (allowUnconfigured) return { success: true, flowConfigured: false };
    throw new Error("HR Applications flow URL is not configured. Configure REACT_APP_HR_APPLICATIONS_LOAD_FLOW_URL / REACT_APP_HR_APPLICATIONS_ACTION_FLOW_URL.");
  }
  let response;
  let data;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({  ...body }),
    });
    const raw = await response.text();
    data = safeJsonParse(raw, { raw });
  } catch (err) {
    throw new Error(`Unable to call HR Applications flow: ${readableValue(err?.message || err)}`);
  }
  if (!response.ok) throw new Error(`HR Applications flow failed (${response.status}): ${readableValue(data?.message || data?.error || data?.raw || data)}`);
  if (data?.success === false) throw new Error(readableValue(data?.message || data?.error || data, "HR Applications flow returned failure."));
  return data || {};
}
export async function loadHRApplicationsData({ currentUser } = {}) {
  const user = normalizeUser(currentUser);
  const data = await postToFlow(HR_APPLICATIONS_LOAD_FLOW_URL, { action: "loadHRApplicationsData", currentUser: user }, { allowUnconfigured: true });
  return {
    documents: parseFlowArray(data?.documents || data?.documentCatalogue || data?.downloads),
    myApplications: parseFlowArray(data?.myApplications || data?.applications),
    approvalQueue: parseFlowArray(data?.approvalQueue || data?.pendingApplications),
    employeeProfile: data?.employeeProfile || data?.profile || {},
    raw: data,
  };
}
export async function saveEmployeeApplication({ currentUser, payload }) {
  return postToFlow(HR_APPLICATIONS_ACTION_FLOW_URL, { action: "saveEmployeeApplication", currentUser: normalizeUser(currentUser), payload });
}
export async function updateEmployeeApplicationStatus({ currentUser, payload }) {
  return postToFlow(HR_APPLICATIONS_ACTION_FLOW_URL, { action: "updateEmployeeApplicationStatus", currentUser: normalizeUser(currentUser), payload });
}
export async function saveDocumentCatalogueItem({ currentUser, payload }) {
  return postToFlow(HR_APPLICATIONS_ACTION_FLOW_URL, { action: "saveDocumentCatalogueItem", currentUser: normalizeUser(currentUser), payload });
}
export async function retireDocumentCatalogueItem({ currentUser, payload }) {
  return postToFlow(HR_APPLICATIONS_ACTION_FLOW_URL, { action: "retireDocumentCatalogueItem", currentUser: normalizeUser(currentUser), payload });
}
export async function uploadHRApplicationFile({ currentUser, filePayload }) {
  return postToFlow(HR_APPLICATIONS_ACTION_FLOW_URL, { action: "uploadApplicationAttachment", currentUser: normalizeUser(currentUser), payload: filePayload });
}
export async function uploadHRDocumentFile({ currentUser, filePayload }) {
  return postToFlow(HR_APPLICATIONS_ACTION_FLOW_URL, { action: "uploadDocumentFile", currentUser: normalizeUser(currentUser), payload: filePayload });
}
